import { useMemo, useRef } from 'react';
import useBoardConfigurations from '../hooks/useBoardConfigurations';
import Board from './Board';
import styles from './Boards.module.scss';
import useLayoutScale from '../hooks/useLayoutScale';
import { StopHeader } from '../models/stopHeader';
import { useTitle } from '../hooks/useTitle';
import useTheme from '../hooks/useTheme';
import { ThemeContext } from '@shared/contexts/ThemeContext';
import { BoardContext } from '@shared/contexts/BoardContext';
import { AudioContext } from '@shared/contexts/AudioContext';
import useAudio from '@shared/hooks/useAudio';
import { useAppSelector } from '@shared/store';
import { useServerSentEvents } from '@shared/hooks/useServerSentEvents';
import AnimationControl from './AnimationControl';
import { useMqtt } from '@modules/vehicle/hooks/useMqtt';
import { ResourceState } from '@shared/utils/createResourceSlice';
import useFeatures from '@shared/hooks/useFeatures';
import useIsTimePassed from '@shared/hooks/useIsTimePassed';
import {
  MILLISECONDS_BETWEEN_ALIVE_MESSAGES,
  MILLISECONDS_OF_ERROR_BEFORE_SHOWING_ERROR_MESSAGE,
  MILLISECONDS_OF_MAXIMUM_EXPECTED_LATENCY,
} from '@shared/constants';

const applicationStartTime = Date.now();

export default function Boards() {
  const theme = useTheme();
  const audio = useAudio();
  const features = useFeatures();

  const { boardConfigurations, error } = useBoardConfigurations();
  useLayoutScale(boardConfigurations?.length);
  useServerSentEvents(boardConfigurations);
  useMqtt(boardConfigurations);
  const stopHeaderStates = useAppSelector((state) => state.stopHeaders);
  const stopHeaders = useMemo(
    () =>
      (Object.values(stopHeaderStates) as ResourceState<StopHeader>[]).map(
        (state) => state.resource,
      ),
    [stopHeaderStates],
  );
  const serverSentEventsLastEventTime = useAppSelector(
    (state) => state.serverSentEvents.lastAliveEventTime,
  );
  const hasServerSentEventsConnectionError = useIsTimePassed(
    serverSentEventsLastEventTime ?? applicationStartTime,
    MILLISECONDS_BETWEEN_ALIVE_MESSAGES +
      MILLISECONDS_OF_MAXIMUM_EXPECTED_LATENCY +
      MILLISECONDS_OF_ERROR_BEFORE_SHOWING_ERROR_MESSAGE,
  );

  const redirecting = useRef(false);
  if (
    !redirecting.current &&
    boardConfigurations?.length === 0 &&
    error === undefined
  ) {
    alert('Måste ange en konfiguration');
    window.location.href =
      'https://www.vasttrafik.se/reseplanering/mer-om-reseplanering/avgangstavla/';
    redirecting.current = true;
  }

  function getDisplayName(header: StopHeader | undefined): string | undefined {
    if (!header) {
      return undefined;
    }

    return header.stopAreaName;
  }
  const title = useMemo(() => {
    const boardDisplayNames = stopHeaders
      .map((stopHeader) => getDisplayName(stopHeader))
      .filter((displayName) => displayName !== undefined);

    if (boardDisplayNames.length === 0) {
      return 'GDS';
    }

    return boardDisplayNames.join(' & ');
  }, [stopHeaders]);
  useTitle(title);

  return (
    <ThemeContext.Provider value={theme}>
      <AudioContext.Provider value={audio}>
        <div className={styles.boards}>
          {boardConfigurations &&
            boardConfigurations.map(
              (boardConfiguration, boardConfigurationIndex) => {
                return (
                  <BoardContext.Provider
                    key={boardConfiguration.id}
                    value={{
                      id: boardConfiguration.id,
                      isFirst: boardConfigurationIndex === 0,
                      isLast:
                        boardConfigurationIndex ===
                        boardConfigurations.length - 1,
                    }}
                  >
                    <Board
                      hasServerSentEventsConnectionError={
                        hasServerSentEventsConnectionError
                      }
                      boardConfiguration={boardConfiguration}
                    />
                  </BoardContext.Provider>
                );
              },
            )}
          {error && <div>{error}</div>}
          {features.animationControl && <AnimationControl />}
        </div>
      </AudioContext.Provider>
    </ThemeContext.Provider>
  );
}
